import { DOMAIN_FOR_LOCALE, FORMATTED_LOCALE, getLocale } from '../../constants';
import { COM_USERCENTRIC_ID, LOCALE_USERCENTRIC_ID } from '../../constants/userCentric';
import { shouldEnableUnifiedDomain } from './utils';
/**
 *
 * This is Domain config for each locale
 * It returns the configurations required for a domain to work under different domains
 * This config is created to support smooth domain migration
 * For example: raisin.fr is migrated to raisin.com/fr-fr
 * For locales who are enabled for both .com and current domain, we need to set all the properties of this config for each locale if shouldEnableUnifiedDomain(locale) returns true
 */
export const SITE_URL = {
    en: 'https://www.raisin.com/en',
    'en-US': 'https://www.raisin.com/en-us',
    'de-DE': 'https://www.weltsparen.de',
    'en-GB': 'https://www.raisin.co.uk',
    'en-IE': 'https://www.raisin.ie',
    'es-ES': 'https://www.raisin.es',
    'nl-NL': 'https://www.raisin.nl',
    'de-AT': 'https://www.weltsparen.at',
    'fr-FR': 'https://www.raisin.fr',
    'pl-PL': 'https://www.raisin.com/pl-pl',
    'fi-FI': 'https://www.raisin.com/fi-fi',
};
const WJIS_BASE_PATH = {
    en: 'raisin.com/wjis/com/v1',
    'en-US': 'raisin.com/wjis/com/v1',
    'de-DE': 'weltsparen.de/dp/public/v1',
    'en-GB': 'raisin.co.uk/dp/public/v1',
    'en-IE': 'raisin.ie/dp/public/v1',
    'es-ES': 'raisin.es/dp/public/v1',
    'nl-NL': 'raisin.nl/dp/public/v1',
    'de-AT': 'weltsparen.at/dp/public/v1',
    'fr-FR': 'raisin.fr/dp/public/v1',
    'pl-PL': 'raisin.com/wjis/pol/v1',
    'fi-FI': 'raisin.com/wjis/fin/v1',
};
const DOMAIN_LOCALE_SUFFIX = {
    en: '',
    'en-US': '',
};
const BASE_PATH = {
    en: '/en',
    'en-US': '/en-us',
};
const REGISTRATION_BASE_PATH = {
    en: '/register/',
    'en-US': '/register/',
};
export const INTEREST_RATE_TYPE_PER_LOCALE = {
    'fr-FR': 'interest_rate_effective',
    'en-GB': 'interest_rate_effective',
    'es-ES': 'interest_rate_effective',
    'en-US': 'interest_rate_effective',
    'en-IE': 'interest_rate_effective',
    en: 'interest_rate_nominal',
    'pl-PL': 'interest_rate_nominal',
    'fi-FI': 'interest_rate_nominal',
    'de-DE': 'interest_rate_nominal',
    'de-AT': 'interest_rate_nominal',
    'nl-NL': 'interest_rate_nominal',
};
export const localisedBankPages = {
    'de-DE': 'bank',
    'en-GB': 'bank',
    'en-IE': 'bank',
    'de-AT': 'bank',
    'fr-FR': 'banque',
    'es-ES': 'bancos',
    'nl-NL': 'banken',
    'pl-PL': 'banki',
    'fi-FI': 'pankki',
};
export const productPageRoutes = {
    'de-DE': 'tagesgeld',
    'de-AT': 'tagesgeld',
    'fr-FR': 'compte-a-terme',
    'es-ES': 'productos',
    'en-IE': 'savings-accounts',
    'en-GB': 'savings-accounts',
    'nl-NL': 'deposito',
    'en-US': 'explore-products',
    'pl-PL': 'lokaty',
    'fi-FI': 'maaraaikaistalletukset',
};
export const getDomainConfig = (locale = 'en') => {
    var _a, _b, _c, _d, _e;
    const formattedLocale = FORMATTED_LOCALE[locale.toLowerCase()] || FORMATTED_LOCALE.en;
    const localeFromCmsFormat = (_a = getLocale(formattedLocale)) === null || _a === void 0 ? void 0 : _a.cmsFormat;
    // eslint-disable-next-line no-useless-escape
    const brokerRegistrationPath = `${REGISTRATION_BASE_PATH[formattedLocale]}\*\*\*\*\*`;
    // eslint-disable-next-line no-useless-escape
    const brokerRegistrationPathWithCmsFormat = `/${localeFromCmsFormat}/register/\*\*\*\*\*`;
    const defaultBrokerRegistrationPath = '/kooperationspartner/register/?regflow=broker&product=%s';
    const integrationServiceIstioProps = {
        integrationServiceDomainName: 'api2',
        integrationServicePath: `raisin.com/wjis/${(_b = getLocale(formattedLocale)) === null || _b === void 0 ? void 0 : _b.iso3}/v1`,
    };
    if (shouldEnableUnifiedDomain(formattedLocale)) {
        //fi,fr,pl
        return Object.assign({ siteUrl: `https://www.raisin.com/${formattedLocale.toLowerCase()}`, domainLocaleSuffix: (_c = DOMAIN_LOCALE_SUFFIX[formattedLocale]) !== null && _c !== void 0 ? _c : `${localeFromCmsFormat}/`, basePath: (_d = BASE_PATH[formattedLocale]) !== null && _d !== void 0 ? _d : `/${localeFromCmsFormat}`, registrationPath: (_e = REGISTRATION_BASE_PATH[formattedLocale]) !== null && _e !== void 0 ? _e : `/${localeFromCmsFormat}/register/`, domain: `raisin.com/${locale.toLowerCase()}`, userCentricId: COM_USERCENTRIC_ID, brokerRegistrationPath: REGISTRATION_BASE_PATH[formattedLocale]
                ? brokerRegistrationPath
                : brokerRegistrationPathWithCmsFormat, interestRateType: INTEREST_RATE_TYPE_PER_LOCALE[formattedLocale], localisedBankPages: localisedBankPages[formattedLocale], productPageRoutes: productPageRoutes[formattedLocale] }, integrationServiceIstioProps);
    }
    const shouldEnableIstio = process.env.NEXT_PUBLIC_ENABLE_WJIS_ISTIO === 'true' ||
        process.env.REACT_APP_ENABLE_WJIS_ISTIO === 'true';
    const integrationServiceProps = shouldEnableIstio
        ? integrationServiceIstioProps
        : {
            integrationServiceDomainName: 'www',
            integrationServicePath: WJIS_BASE_PATH[formattedLocale],
        };
    return Object.assign({ siteUrl: SITE_URL[formattedLocale], domainLocaleSuffix: '', basePath: '', registrationPath: '/register/', domain: DOMAIN_FOR_LOCALE[locale], userCentricId: LOCALE_USERCENTRIC_ID[formattedLocale], brokerRegistrationPath: defaultBrokerRegistrationPath, interestRateType: INTEREST_RATE_TYPE_PER_LOCALE[formattedLocale], localisedBankPages: localisedBankPages[formattedLocale], productPageRoutes: productPageRoutes[formattedLocale] }, integrationServiceProps);
};
//# sourceMappingURL=config.js.map